import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Broadcast, ArrowClockwise, SpinnerGap, Sparkle, Prohibit, Clock, Warning,
  ArrowSquareOut, Buildings, ArrowRight, Info, CheckCircle,
} from "@phosphor-icons/react";

const TYPE_META = {
  new: { label: "New product", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Sparkle },
  discontinued: { label: "Discontinued", cls: "bg-red-50 text-red-700 border-red-200", Icon: Prohibit },
  discontinuing_soon: { label: "Discontinuing soon", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
};

const CR_META = {
  pending: { label: "Request pending", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
}

function SummaryStat({ label, value, tone }) {
  const tones = {
    emerald: "text-emerald-700", red: "text-red-700", amber: "text-amber-700", blue: "text-blue-700",
  };
  return (
    <div className="border border-slate-200 bg-white rounded-md p-4">
      <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">{label}</div>
      <div className={`text-3xl font-heading font-bold mt-1 ${tones[tone] || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

export default function ProductRadar() {
  const [status, setStatus] = useState(null);
  const [scan, setScan] = useState(null);
  const [findings, setFindings] = useState([]);
  const [mfrs, setMfrs] = useState([]);
  const [selectedMfr, setSelectedMfr] = useState("all");
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const running = scan?.status === "running" || status?.running;

  const loadFindings = async () => {
    try {
      const { data } = await api.get("/product-radar/findings");
      setFindings(data.findings || []);
    } catch { /* ignore */ }
  };

  const loadScan = async () => {
    const { data } = await api.get("/product-radar/scan/latest");
    setScan(data && data.id ? data : null);
    return data;
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: st }, { data: ms }] = await Promise.all([
        api.get("/product-radar/status"),
        api.get("/manufacturers"),
      ]);
      setStatus(st);
      setMfrs(ms || []);
      await loadScan();
      await loadFindings();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load Product Radar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  // Poll while a scan is running
  useEffect(() => {
    if (running && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const s = await loadScan();
        if (!s || s.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          await loadFindings();
          try { const { data } = await api.get("/product-radar/status"); setStatus(data); } catch { /* */ }
          if (s && s.status === "done") toast.success("Scan complete");
          if (s && s.status === "error") toast.error("Scan failed — see details below");
        }
      }, 5000);
    }
    return () => { if (!running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [running]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const runScan = async () => {
    setStarting(true);
    try {
      const body = selectedMfr === "all" ? {} : { manufacturer_ids: [selectedMfr] };
      const { data } = await api.post("/product-radar/scan", body);
      if (data.already_running) toast.info("A scan is already running");
      else toast.success("Scan started — this can take a minute or two");
      await loadScan();
      setFindings([]);
      setStatus((st) => ({ ...(st || {}), running: true }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setStarting(false);
    }
  };

  const notConfigured = status && (!status.firecrawl_configured || !status.llm_configured);
  const summary = scan?.summary || { new: 0, discontinued: 0, discontinuing_soon: 0, requests_created: 0 };

  // Group findings by manufacturer
  const grouped = findings.reduce((acc, f) => {
    (acc[f.manufacturer_name || "—"] = acc[f.manufacturer_name || "—"] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="p-4 sm:p-8 max-w-[1200px]" data-testid="product-radar-page">
      <header className="mb-8">
        <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Insights · AI</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <Broadcast size={32} weight="fill" /> Product Radar
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-3xl">
          Scans the web live for each of your manufacturers and flags <strong>new products</strong> you don't stock yet and
          products that are <strong>discontinued</strong> (or winding down). Findings are filed into
          {" "}<Link to="/update-requests" className="text-blue-600 hover:underline">Update Requests</Link> for your approval.
        </p>
      </header>

      {/* AI disclaimer */}
      <div className="flex items-start gap-2 border border-slate-200 bg-slate-50 text-slate-600 rounded-md px-4 py-3 mb-5 text-[13px]" data-testid="radar-disclaimer">
        <Info size={18} className="shrink-0 mt-0.5 text-slate-400" />
        <span>AI suggestions from live web scraping. They can be incomplete or occasionally wrong — <strong>always verify a finding before approving</strong> its request.</span>
      </div>

      {/* Not-configured warning */}
      {notConfigured && (
        <div className="flex items-start gap-2 border border-amber-200 bg-amber-50 text-amber-800 rounded-md px-4 py-3 mb-5 text-sm" data-testid="radar-not-configured">
          <Warning size={18} className="shrink-0 mt-0.5" />
          <span>
            {!status.firecrawl_configured && "Web scraping (Firecrawl) is not configured. "}
            {!status.llm_configured && "The AI key is not configured. "}
            Scans can't run until this is set up on the server.
          </span>
        </div>
      )}

      {/* Controls */}
      <div className="border border-slate-200 bg-white rounded-md p-5 mb-6 flex flex-wrap items-end gap-4" data-testid="radar-controls">
        <div className="space-y-1.5">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Scan scope</div>
          <Select value={selectedMfr} onValueChange={setSelectedMfr} disabled={running}>
            <SelectTrigger className="w-64" data-testid="radar-manufacturer-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All manufacturers</SelectItem>
              {mfrs.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={runScan}
          disabled={starting || running || notConfigured}
          className="bg-slate-900 hover:bg-slate-800 gap-2 rounded-md h-10"
          data-testid="run-scan-btn"
        >
          {running ? <SpinnerGap size={16} className="animate-spin" /> : <ArrowClockwise size={16} />}
          {running ? "Scanning…" : starting ? "Starting…" : "Run scan"}
        </Button>
        {scan?.finished_at && !running && (
          <div className="text-xs text-slate-500 ml-auto">Last scan: {fmtWhen(scan.finished_at)}</div>
        )}
      </div>

      {/* Running progress */}
      {running && (
        <div className="border border-blue-200 bg-blue-50/60 rounded-md p-5 mb-6" data-testid="radar-scanning">
          <div className="flex items-center gap-2 text-blue-800 font-medium mb-2">
            <SpinnerGap size={18} className="animate-spin" /> Scanning the web…
          </div>
          <div className="text-sm text-slate-600">
            {scan?.current ? <>Currently checking <strong>{scan.current}</strong>. </> : "Starting up… "}
            {typeof scan?.manufacturers_total === "number" && scan.manufacturers_total > 0 && (
              <>Progress: {scan.manufacturers_done || 0} of {scan.manufacturers_total} brands.</>
            )}
          </div>
          {scan?.manufacturers_total > 0 && (
            <div className="mt-3 h-2 bg-blue-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${Math.round(100 * (scan.manufacturers_done || 0) / scan.manufacturers_total)}%` }} />
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : !scan ? (
        <div className="border border-dashed border-slate-300 bg-white p-16 text-center rounded-md" data-testid="radar-empty">
          <Broadcast size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500 max-w-md mx-auto">No scans yet. Pick a scope and hit <strong>Run scan</strong> to let the AI check your manufacturers for new & discontinued products.</div>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="radar-summary">
            <SummaryStat label="New products" value={summary.new || 0} tone="emerald" />
            <SummaryStat label="Discontinued" value={summary.discontinued || 0} tone="red" />
            <SummaryStat label="Discontinuing soon" value={summary.discontinuing_soon || 0} tone="amber" />
            <SummaryStat label="Approval requests" value={summary.requests_created || 0} tone="blue" />
          </div>

          {(summary.requests_created || 0) > 0 && (
            <Link to="/update-requests" className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline mb-6" data-testid="radar-goto-requests">
              Review {summary.requests_created} approval request{summary.requests_created === 1 ? "" : "s"} in Update Requests <ArrowRight size={14} />
            </Link>
          )}

          {/* Scan errors */}
          {scan.errors?.length > 0 && (
            <div className="border border-amber-200 bg-amber-50 rounded-md p-4 mb-6 text-sm text-amber-800" data-testid="radar-errors">
              <div className="font-medium mb-1 flex items-center gap-1.5"><Warning size={16} /> Some brands couldn't be scanned</div>
              <ul className="list-disc ml-5 space-y-0.5">
                {scan.errors.map((er, i) => <li key={i}><strong>{er.manufacturer}</strong>: {er.error}</li>)}
              </ul>
            </div>
          )}

          {/* Findings */}
          {findings.length === 0 ? (
            <div className="border border-dashed border-slate-300 bg-white p-12 text-center rounded-md" data-testid="radar-no-findings">
              <CheckCircle size={28} className="mx-auto text-slate-300 mb-3" />
              <div className="text-sm text-slate-500">
                {scan.status === "running" ? "Scan in progress…" : "No new or discontinued products found in this scan."}
              </div>
            </div>
          ) : (
            <div className="space-y-6" data-testid="radar-findings">
              {Object.entries(grouped).map(([brand, list]) => (
                <div key={brand} className="border border-slate-200 bg-white rounded-md overflow-hidden" data-testid={`radar-brand-${brand}`}>
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                    <Buildings size={16} className="text-slate-500" />
                    <span className="font-heading font-semibold text-sm text-slate-800">{brand}</span>
                    <span className="text-xs text-slate-400 font-mono">· {list.length} finding{list.length === 1 ? "" : "s"}</span>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {list.map((f) => {
                      const meta = TYPE_META[f.type] || TYPE_META.new;
                      const TypeIcon = meta.Icon;
                      const cr = f.change_request_status ? CR_META[f.change_request_status] : null;
                      return (
                        <li key={f.id} className="px-4 py-3 flex items-start gap-3" data-testid={`finding-${f.id}`}>
                          {f.color_hex ? (
                            <div className="w-5 h-5 border border-slate-200 rounded-sm shrink-0 mt-0.5" style={{ background: f.color_hex }} />
                          ) : (
                            <div className="w-5 h-5 shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`text-[9px] font-mono tracking-[0.12em] uppercase px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 ${meta.cls}`}>
                                <TypeIcon size={11} weight="fill" /> {meta.label}{f.type === "discontinuing_soon" && f.discontinued_date ? ` · ${f.discontinued_date}` : ""}
                              </span>
                              <span className="font-medium text-slate-900">{f.product_name}</span>
                              {f.series_name && <span className="text-xs text-slate-400 font-mono">{f.series_name}</span>}
                            </div>
                            {f.note && <div className="text-[13px] text-slate-600 mt-1">{f.note}</div>}
                            <div className="flex flex-wrap items-center gap-3 mt-1.5">
                              {cr ? (
                                <Link to="/update-requests" className={`text-[10px] font-mono tracking-[0.1em] uppercase px-1.5 py-0.5 rounded-sm border ${cr.cls}`} data-testid={`finding-cr-${f.id}`}>
                                  {cr.label}
                                </Link>
                              ) : (
                                <span className="text-[10px] font-mono tracking-[0.1em] uppercase px-1.5 py-0.5 rounded-sm border bg-slate-50 text-slate-400 border-slate-200">
                                  No request {f.type === "new" ? "(needs a series)" : "(not in your catalogue)"}
                                </span>
                              )}
                              {f.evidence_url && (
                                <a href={f.evidence_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1" data-testid={`finding-src-${f.id}`}>
                                  Source <ArrowSquareOut size={12} />
                                </a>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
